import { useFloating, autoUpdate, offset, inline } from '@floating-ui/react';
import { AllSelection, TextSelection } from '@tiptap/pm/state';
import { type Editor, isNodeSelection } from '@tiptap/react';
import { AnimatePresence, motion } from 'motion/react';
import { CellSelection } from 'prosemirror-tables';
import {
  type ReactNode,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { cn } from '@/lib/utils';

import {
  useEditorContainer,
  useEditorFloatingContainer,
} from '../base-editor-context';
import { useEditorState } from '../hooks/useEditorState';
import { getSelectionElement } from '../hooks/useSelectionElement';
import { editorEventBus } from '../utils/event';
import { stickyHideWithScroll } from '../utils/floating-ui';
import { trackSelectionPointerActivity } from '../utils/track-selection-pointer-activity';

function isElementInViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return (
    element.isConnected &&
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

export function getBubbleSelectionRange(editor: Editor) {
  const selection = editor.state.selection;
  // Select-all includes block boundaries; anchor to the text inside them.
  const { from, to } =
    selection instanceof AllSelection
      ? TextSelection.between(selection.$from, selection.$to)
      : selection;
  const domFrom = editor.view.domAtPos(from);
  const domTo = editor.view.domAtPos(to);
  const range = document.createRange();
  range.setStart(domFrom.node, domFrom.offset);
  range.setEnd(domTo.node, domTo.offset);
  return range;
}

export function getBubbleSelectionReference(
  editor: Editor,
  isOpen: () => boolean
) {
  const selectionElement = getSelectionElement(editor);
  const range = getBubbleSelectionRange(editor);
  let rect = range.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  let rects = range.getClientRects();
  const measure = () => {
    if (
      !isOpen() ||
      editor.isDestroyed ||
      editor.isEmpty ||
      editor.state.selection.empty ||
      !range.commonAncestorContainer.isConnected
    )
      return;
    const nextRect = range.getBoundingClientRect();
    if (!nextRect.width || !nextRect.height) return;
    rect = nextRect;
    rects = range.getClientRects();
  };
  // Measure while open; retain the last valid bounds for the exit animation.
  return {
    getBoundingClientRect: () => {
      measure();
      return rect;
    },
    getClientRects: () => {
      measure();
      return rects;
    },
    contextElement: selectionElement ?? undefined,
  };
}

/**
 * Hook to manage bubble menu positioning and visibility
 */
function useEditorBubbleMenu() {
  const editor = useEditorState();
  const editorContainer = useEditorContainer();
  const floatingContainer = useEditorFloatingContainer();

  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const isEditorVisibleRef = useRef(true);
  const isUserFocusedRef = useRef(true);

  useEffect(() => {
    if (!editor) return;
    const editorElement = editor.view.dom;
    isEditorVisibleRef.current = isElementInViewport(editorElement);

    const observer = new IntersectionObserver(
      ([entry]) => {
        isEditorVisibleRef.current = entry.isIntersecting;
        if (!entry.isIntersecting) {
          setIsOpen(false);
          editorEventBus.emit('EDITOR_BUBBLE_WILL_CLOSE', { data: undefined });
        }
      },
      { threshold: 0 }
    );
    observer.observe(editorElement);
    return () => observer.disconnect();
  }, [editor]);
  const { x, y, refs, floatingStyles, update, middlewareData } = useFloating({
    open: isOpen,
    placement: 'top',
    strategy: 'absolute',
    middleware: [
      inline(),
      offset(8),
      ...(editorContainer
        ? [
            stickyHideWithScroll({
              boundary: editorContainer,
              padding: { top: 15, right: 10, bottom: 10, left: 10 },
              stickyThreshold: 75,
            }),
          ]
        : []),
    ],
  });

  // Trigger middleware update on scroll
  useEffect(() => {
    if (!editorContainer || !isOpen) return;

    const handleScroll = () => {
      update();
    };

    editorContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => editorContainer.removeEventListener('scroll', handleScroll);
  }, [editorContainer, isOpen, update]);

  const updatePosition = useCallback(() => {
    if (!editor || editor.isDestroyed) return;

    try {
      // The shared editor can be mid-reparent between fields, briefly leaving
      // its view unmounted. Tiptap throws on view access in that window, so
      // bail on this position update rather than crash.
      const reference = getBubbleSelectionReference(
        editor,
        () => isOpenRef.current
      );
      if (reference) refs.setReference(reference);
    } catch {
      // View not mounted yet; skip this update.
    }
  }, [editor, refs]);

  const updateVisibility = useDebouncedCallback(
    () => {
      if (!editor) return;

      const { state } = editor;
      const { selection } = state;
      const { empty } = selection;
      const isEditorVisible =
        isEditorVisibleRef.current || isElementInViewport(editor.view.dom);
      if (isEditorVisible) isEditorVisibleRef.current = true;

      // Don't show if:
      // - Selection is empty
      // - Editor not editable
      // - Node selection (for drag handles)
      // - Cell selection (multi-cell table selection)
      // - Inside image, code block
      const shouldShow =
        !empty &&
        !editor.isEmpty &&
        isUserFocusedRef.current &&
        editor.isEditable &&
        isEditorVisible &&
        !isNodeSelection(selection) &&
        !(selection instanceof CellSelection) &&
        !editor.isActive('image') &&
        !editor.isActive('codeBlock');

      if (!shouldShow) {
        setIsOpen(false);
        editorEventBus.emit('EDITOR_BUBBLE_WILL_CLOSE', { data: undefined });
      } else {
        updatePosition();
        setIsOpen(true);
      }
    },
    {
      delay: 75,
      flushOnUnmount: true,
    }
  );

  // Update visibility based on editor selection
  useEffect(() => {
    if (!editor) {
      setIsOpen(false);
      editorEventBus.emit('EDITOR_BUBBLE_WILL_CLOSE', { data: undefined });
      return;
    }

    // Update on selection change
    updateVisibility();
    editor.on('selectionUpdate', updateVisibility);
    editor.on('transaction', updateVisibility);
    editor.on('update', updateVisibility);

    const handleFocus = () => {
      isUserFocusedRef.current = true;
    };
    editor.on('focus', handleFocus);

    return () => {
      editor.off('selectionUpdate', updateVisibility);
      editor.off('transaction', updateVisibility);
      editor.off('update', updateVisibility);
      editor.off('focus', handleFocus);
    };
  }, [editor, updateVisibility]);

  // Track when user is actively selecting text
  useEffect(() => {
    if (!editor) return;
    return trackSelectionPointerActivity(
      editor,
      isUserFocusedRef,
      updateVisibility
    );
  }, [editor, updateVisibility]);

  // Pointerdown instead of blur: once focus is already outside the editor
  // (e.g. dropdown open), blur won't fire again on subsequent outside clicks.
  useEffect(() => {
    if (!isOpen || !editor) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (editor.view.dom.contains(target)) return;
      if (refs.floating.current?.contains(target)) return;
      if (floatingContainer?.contains(target)) return;
      if (target.closest('[data-radix-popper-content-wrapper]')) return;

      isUserFocusedRef.current = false;
      setIsOpen(false);
      editorEventBus.emit('EDITOR_BUBBLE_WILL_CLOSE', { data: undefined });
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen, editor, refs, floatingContainer]);

  // Set up autoUpdate to track scroll/resize
  useEffect(() => {
    if (!isOpen || !refs.floating.current || !editor) {
      return;
    }

    // Get the context element (actual DOM element) for scroll tracking
    const virtualRef = refs.reference.current;
    const contextElement =
      virtualRef &&
      typeof virtualRef === 'object' &&
      'contextElement' in virtualRef
        ? virtualRef.contextElement
        : null;

    if (!contextElement) return;

    const cleanup = autoUpdate(contextElement, refs.floating.current, () => {
      updatePosition();
      update();
    });

    return cleanup;
  }, [isOpen, refs, update, updatePosition, editor]);

  return {
    x,
    y,
    isOpen,
    refs,
    floatingStyles,
    middlewareData,
  };
}

export interface EditorBubbleProps {
  readonly children: ReactNode;
}

export const EditorBubble = ({ children }: EditorBubbleProps) => {
  const editorContainer = useEditorContainer();
  const floatingContainer = useEditorFloatingContainer();
  const { isOpen, refs, floatingStyles, middlewareData } =
    useEditorBubbleMenu();

  if (!editorContainer || !floatingContainer) return;

  const isHidden = middlewareData.stickyHideWithScroll?.referenceHidden;
  const { isSticky, stickyEdge, stickyViewportPosition } =
    middlewareData.stickyHideWithScroll ?? {};

  const bubbleContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={
            isSticky
              ? {
                  y: stickyEdge.top ? -2 : 4,
                  opacity: 1,
                }
              : { opacity: 0, scale: 0.95 }
          }
          animate={{
            y: 0,
            opacity: isHidden ? 0 : 1,
            scale: isHidden ? 0.95 : 1,
          }}
          exit={{
            opacity: 0,
            scale: 0.95,
            transition: {
              type: 'keyframes',
              ease: 'easeIn',
              duration: 0.1,
            },
          }}
          transition={{
            type: 'keyframes',
            ease: 'easeOut',
            duration: 0.15,
          }}
          data-editor-bubble-menu=""
          className={cn(
            'pointer-events-auto',
            'flex items-center gap-1',
            'w-fit max-w-[90vw] h-9',
            'overflow-hidden',
            'rounded-lg smoothing-default',
            'border border-border-strong',
            'bg-surface',
            'px-1.5 py-1',
            'shadow-xl'
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Container version - always rendered in scroll container, hidden when sticky
  const containerVersion = createPortal(
    <div
      className="z-10"
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        opacity: isSticky ? 0 : 1,
        pointerEvents: isSticky ? 'none' : 'auto',
      }}
    >
      {bubbleContent}
    </div>,
    editorContainer
  );

  // Floating clone - only when sticky, portaled to floating container
  const floatingClone =
    isSticky && stickyViewportPosition
      ? createPortal(
          <div
            className="z-10"
            style={{
              position: 'fixed',
              left: stickyViewportPosition.x,
              top: stickyViewportPosition.y,
              transform: 'none',
            }}
          >
            {bubbleContent}
          </div>,
          floatingContainer
        )
      : null;

  return (
    <>
      {containerVersion}
      {floatingClone}
    </>
  );
};
